/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, ComponentType } from 'react';
import { DecoratorFn, StoryContext as StorybookContext } from '@storybook/react';
import { registerAemComponents } from './register-aem-components';
import { Model, ModelManager } from '@adobe/aem-spa-page-model-manager';

// Define the expected model shape
interface AemModel extends Model {
  [key: string]: any; // Allow any additional properties
  ':type'?: string;
  ':hierarchyType'?: string;
  ':path'?: string;
  ':items'?: { [key: string]: any };
  ':itemsOrder'?: string[];
  ':children'?: { [key: string]: any };
}

// Initialize ModelManager with a proper configuration
const initializeModelManager = () => {
  if (!(window as any).aemModelManagerInitialized) {
    const modelClient = {
      _apiHost: '',
      get apiHost() {
        return this._apiHost;
      },
      set apiHost(host: string) {
        this._apiHost = host;
      },
      fetch: (): Promise<AemModel> => {
        return Promise.resolve({
          ':type': 'spa:root',
          ':hierarchyType': 'root',
          ':path': '/',
          ':items': {},
          ':itemsOrder': [],
          ':children': {},
        });
      },
      destroy: () => {
        // No-op for storybook
      },
    };

    const initialModel: AemModel = {
      ':type': 'spa:root',
      ':hierarchyType': 'root',
      ':path': '/',
      ':items': {},
      ':itemsOrder': [],
      ':children': {},
    };

    ModelManager.initialize({
      modelClient: modelClient as any, // Cast to any to avoid type issues with private ModelClient
      model: initialModel,
    });
    (window as any).aemModelManagerInitialized = true;
    return true;
  }
  return false;
};

// Function to register AEM components
export const initializeAemComponents = () => {
  try {
    initializeModelManager();
    registerAemComponents();
    return true;
  } catch (error) {
    console.error('[AEMDecorator] Failed to initialize AEM components:', error);
    return false;
  }
};

// Wrapper component to handle initialization
const AemInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!(window as any).aemComponentsInitialized) {
      (window as any).aemComponentsInitialized = initializeAemComponents();
    }
    setInitialized(true);
  }, []);

  return initialized ? <>{children}</> : null;
};

// Extend Storybook's context type with our AEM mappings
interface AemParameters {
  aemMappings?: Record<string, ComponentType>;
  [key: string]: any;
}

interface AemStoryContext extends StorybookContext {
  parameters: AemParameters;
}

// Story decorator factory
export const withAemComponents = (customMapping: Record<string, ComponentType> = {}) => {
  const decorator: DecoratorFn = (StoryFn, context: AemStoryContext) => {
    // Get component mappings from story parameters or use default
    const storyMappings = context.parameters['aemMappings'] || {};
    const allMappings = { ...customMapping, ...storyMappings };
    console.log('[AEMDecorator] Custom Mappings received via decorator', customMapping);
    console.log('[AEMDecorator] Story Mappings received via parameters', storyMappings);

    // Register components with the provided mappings
    if (Object.keys(allMappings).length > 0) {
      registerAemComponents(allMappings);
    } else if (typeof window !== 'undefined' && !(window as any).aemComponentsInitialized) {
      // Fallback to default initialization if no mappings provided
      initializeAemComponents();
    }

    return (
      <AemInitializer>
        <StoryFn {...context} />
      </AemInitializer>
    );
  };

  return decorator as unknown as DecoratorFn;
};
